import React, { useState, useMemo, useCallback } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import Overview from './components/Overview';
import ModeratorPerformance from './components/ModeratorPerformance';
import StorePerformance from './components/StorePerformance';
import ProductInsights from './components/ProductInsights';
import CompetitorAnalysis from './components/CompetitorAnalysis';
import AccessDenied from './components/AccessDenied';
import CoPilot from './components/CoPilot';
import ApiTest from './components/ApiTest';
import authService from './services/authService';
import VoucherSearch from './components/VoucherSearch';

// Dashboard component moved outside App to prevent recreation on every render
const Dashboard = ({ 
  dateRange, 
  onDateRangeChange, 
  onSearchClick, 
  searchModalOpen, 
  onSearchClose,
  onOpenStoreDrillDown,
  onOpenValidatorDrillDown,
  onRegisterDrillDownTrigger
}) => (
  <>
    {/* Header with Banner and Date Selector */}
    <Header onDateChange={onDateRangeChange} />
    
    {/* Main Dashboard Container */}
    <div className="container">
      {/* Overview Section */}
      <Overview dateRange={dateRange} onSearchClick={onSearchClick} />
      
      {/* Validator Performance Insights */}
      <ModeratorPerformance dateRange={dateRange} onRegisterDrillDownTrigger={onRegisterDrillDownTrigger} />
      
      {/* Store Performance (includes Store Based Information and Store Performance Insights) */}
      <StorePerformance dateRange={dateRange} onRegisterDrillDownTrigger={onRegisterDrillDownTrigger} />
      
      {/* Products & Purchase Insights + Share of Basket */}
      <ProductInsights dateRange={dateRange} />
      
      {/* Competitor & Complementary Products */}
      <CompetitorAnalysis dateRange={dateRange} />
    </div>

    {/* Voucher Search Modal */}
    <VoucherSearch 
      isOpen={searchModalOpen} 
      onClose={onSearchClose}
      onOpenStoreDrillDown={onOpenStoreDrillDown}
      onOpenValidatorDrillDown={onOpenValidatorDrillDown}
      dateRange={dateRange}
    />
  </>
);

function App() {
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
    dateFilterType: 'Redemption'
  });

  // State for voucher search modal
  const [searchModalOpen, setSearchModalOpen] = useState(false);

  // State for drill-down triggers from components
  const [drillDownTriggers, setDrillDownTriggers] = useState({
    store: null,
    validator: null
  });

  // React Router hook - must be called before any conditional returns
  const location = useLocation();

  // Memoize dateRange to prevent unnecessary re-renders
  const memoizedDateRange = useMemo(() => dateRange, [dateRange.startDate, dateRange.endDate, dateRange.dateFilterType]);

  // Memoize callbacks to prevent child component re-renders
  const handleDateRangeChange = useCallback((newDateRange) => {
    setDateRange(prev => {
      const merged = { ...prev, ...newDateRange };
      if (
        prev.startDate === merged.startDate &&
        prev.endDate === merged.endDate &&
        prev.dateFilterType === merged.dateFilterType
      ) {
        return prev;
      }
      return merged;
    });
  }, []);

  const handleLogout = useCallback(() => {
    authService.logout();
  }, []);

  const handleSearchClick = useCallback(() => {
    setSearchModalOpen(true);
  }, []);

  const handleSearchClose = useCallback(() => {
    setSearchModalOpen(false);
  }, []);

  const handleRegisterDrillDownTrigger = useCallback((type, triggerFunction) => {
    setDrillDownTriggers(prev => ({
      ...prev,
      [type]: triggerFunction
    }));
  }, []);

  const handleOpenStoreDrillDown = useCallback((storeData) => {
    if (drillDownTriggers.store) {
      drillDownTriggers.store(storeData.name || storeData.retailer);
    }
    setSearchModalOpen(false);
  }, [drillDownTriggers.store]);

  const handleOpenValidatorDrillDown = useCallback((validatorData) => {
    if (drillDownTriggers.validator) {
      drillDownTriggers.validator(validatorData.name || validatorData.moderator);
    }
    setSearchModalOpen(false);
  }, [drillDownTriggers.validator]);

  // Check if user has required access (after all hooks)
  const hasAccess = authService.hasRequiredAccess();

  // If user doesn't have access, show access denied page
  if (!hasAccess) {
    return <AccessDenied />;
  }

  // Get user information
  const userInfo = authService.getUserInfo();
  const userName = userInfo?.preferred_username || userInfo?.name || 'User';

  return (
    <div className="App">
      {/* Authentication Bar */}
      <div style={{
        backgroundColor: '#2c3e50',
        color: 'white',
        padding: '8px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '14px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div>
            Welcome, <strong>{userName}</strong>
          </div>
          <nav style={{ display: 'none', gap: '15px', marginLeft: '30px' }}>
            <Link 
              to="/"
              style={{
                color: location.pathname === '/' ? '#4CAF50' : 'white',
                textDecoration: 'none',
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: '#2c3e50',
                border: 'none',
                outline: 'none',
                transition: 'all 0.2s'
              }}
            >
              Dashboard
            </Link>
            <Link 
              to="/co-pilot"
              style={{
                color: location.pathname === '/co-pilot' ? '#4CAF50' : 'white',
                textDecoration: 'none',
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: '#2c3e50',
                border: 'none',
                outline: 'none',
                transition: 'all 0.2s'
              }}
            >
              Co-Pilot
            </Link>
            <Link 
              to="/api-test"
              style={{
                color: location.pathname === '/api-test' ? '#FF9800' : 'white',
                textDecoration: 'none',
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: '#2c3e50',
                border: 'none',
                outline: 'none',
                transition: 'all 0.2s'
              }}
            >
              🔧 API Test
            </Link>
          </nav>
        </div>
        <button
          onClick={handleLogout}
          style={{
            backgroundColor: 'transparent',
            border: '1px solid white',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = 'rgba(255,255,255,0.1)';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = 'transparent';
          }}
        >
          Logout
        </button>
      </div>

      {/* Routes */}
      <Routes>
        <Route 
          path="/" 
          element={
            <Dashboard 
              dateRange={memoizedDateRange}
              onDateRangeChange={handleDateRangeChange}
              onSearchClick={handleSearchClick}
              searchModalOpen={searchModalOpen}
              onSearchClose={handleSearchClose}
              onOpenStoreDrillDown={handleOpenStoreDrillDown}
              onOpenValidatorDrillDown={handleOpenValidatorDrillDown}
              onRegisterDrillDownTrigger={handleRegisterDrillDownTrigger}
            />
          } 
        />
        <Route path="/co-pilot" element={<CoPilot />} />
        <Route path="/api-test" element={<ApiTest />} />
      </Routes>
    </div>
  );
}

export default App; 